<script lang="ts" setup>
import type { MemberPageConfig } from '../configs/types'
import { useUserStore } from '@/stores'
import { useSubscription } from '@/composables/payment'
import { tripartiteLoginPage } from '@/composables/useTripartiteLogin'
import { useElementClick } from '@/composables/tracks/useElementClick'
import { prefixCDN } from '@/utils/cdn'

interface Props {
  config: MemberPageConfig
}

defineProps<Props>()

const userStore = useUserStore()
const { elementClick } = useElementClick()
const { subscribeProducts } = useSubscription('member')

function login() {
  elementClick('login')
  tripartiteLoginPage('Profile')
}
</script>

<template>
  <div class="page-cover">
    <!-- 品牌行 -->
    <div class="brand-row flex items-center px-15px">
      <img src="/images/vowpath/home/logo.png" class="brand-logo" alt="Vowpath">
    </div>
    <!-- 头像 -->
    <div class="px-20px pt-16px pb-24px flex items-center justify-between">
      <div class="shrink-0">
        <img v-if="userStore.$state.headImageUrl" class="w-66px h-66px rounded-full avatar-ring" :src="prefixCDN(userStore.$state.thirdHeadImgUrl)" alt="avatar">
        <!-- 默认头像切图自带渐变底与描边 -->
        <img v-else class="w-66px h-66px" src="/images/vowpath/member/avatar-default.png" alt="avatar">
      </div>

      <div class="flex-1 overflow-hidden px-12px">
        <div class="flex items-center width-full">
          <div class="username overflow-hidden whitespace-nowrap text-ellipsis">
            {{ userStore.$state.thirdNickName || userStore.$state.email || userStore.$state.memberName }}
          </div>
          <div v-if="userStore.$state.subscribe" class="shrink-0">
            <img src="../icons/vowpath/vip.png" class="w-18px h-14px ml-8px">
          </div>
        </div>
        <p class="uid">
          UID {{ userStore.$state.code }}
        </p>
      </div>

      <div v-if="!userStore.$state.thirdPartType" type="link" class="shrink-0">
        <a class="flex items-center text-15px sign-in-link" @click="login">
          {{ config.texts.signIn }}
          <i-icon-park-outline-right class="ml-2px" />
        </a>
      </div>
    </div>

    <!-- vip 订阅 -->
    <div v-if="subscribeProducts?.length || userStore.$state.subscribe" class="vip-cover flex items-center justify-between mx-10px px-16px">
      <div class="flex-1 flex items-center">
        <div>
          <div class="vip-title">
            <span v-if="!userStore.$state.subscribe">{{ config.texts.getVip }}</span>
            <span v-else>{{ config.texts.vipMembership }}</span>
          </div>
          <div v-if="!userStore.$state.subscribe" class="vip-subtitle">
            {{ config.texts.moreCoinsMoreBenefits }}
          </div>
          <div v-if="userStore.$state.subscribeEndDate" class="vip-subtitle">
            {{ config.texts.expires }} {{ $d(new Date(userStore.$state.subscribeEndDate), 'short') }}
          </div>
        </div>
      </div>
      <router-link
        v-if="!userStore.$state.subscribe"
        :to="{ name: 'subscribe' }"
      >
        <div class="details-btn flex items-center justify-center" @click="elementClick('Details')">
          {{ config.texts.details }}
        </div>
      </router-link>
    </div>

    <!-- 金币余额卡片 -->
    <!-- v-if="userStore.$state.thirdPartType"  -->
    <div class="coin-card mx-10px px-16px flex items-center justify-between">
      <div class="flex items-center">
        <img src="/images/vowpath/home/icon-diamond.png" class="coin-diamond shrink-0" alt="">
        <span class="coin-balance-text">{{ userStore.coinBalance }}</span>
      </div>
      <router-link :to="{ name: 'pay' }">
        <div class="top-up-btn flex items-center justify-center" @click="elementClick('TopUp')">
          Top Up
        </div>
      </router-link>
    </div>

    <!-- setting -->
    <div class="menu-card mt-10px mx-10px px-20px">
      <div>
        <router-link
          :to="{ name: 'recharge' }"
          class="w-full h-61px flex items-center justify-between"
        >
          <div class="flex items-center space-x-7px">
            <img src="/images/vowpath/member/icon-transaction-new.png" width="21px" height="21px" alt="">
            <p class="text-n-text-1">
              {{ config.texts.transactionHistory }}
            </p>
          </div>
          <i-icon-park-outline-right class="text-n-text-3" />
        </router-link>
      </div>
      <div>
        <router-link
          :to="{ name: 'consume' }"
          class="w-full h-61px flex items-center justify-between border-t border-n-border-primary"
        >
          <div class="flex items-center space-x-7px">
            <img src="/images/vowpath/member/icon-unlock-new.png" width="21px" height="21px" alt="">
            <p class="text-n-text-1">
              {{ config.texts.chaptersUnlocked }}
            </p>
          </div>
          <i-icon-park-outline-right class="text-n-text-3" />
        </router-link>
      </div>
      <div>
        <router-link :to="{ name: 'contact-us' }">
          <div class="w-full h-61px flex items-center justify-between border-t border-n-border-primary" @click="elementClick('contact_us')">
            <div class="flex items-center space-x-7px">
              <img src="/images/vowpath/member/icon-contact-new.png" width="21px" height="21px" alt="">
              <p class="text-n-text-1">
                {{ config.texts.contactUs }}
              </p>
            </div>
            <i-icon-park-outline-right class="text-n-text-3" />
          </div>
        </router-link>
      </div>
      <div>
        <router-link :to="{ name: 'setting' }">
          <div class="w-full h-61px flex items-center justify-between border-t border-n-border-primary" @click="elementClick('setting')">
            <div class="flex items-center space-x-7px">
              <img src="/images/vowpath/member/icon-setting-new.png" width="21px" height="21px" alt="">
              <p class="text-n-text-1">
                {{ config.texts.setting }}
              </p>
            </div>
            <i-icon-park-outline-right class="text-n-text-3" />
          </div>
        </router-link>
      </div>
    </div>

    <BasicTabbar tab="member" />
  </div>
</template>

<style scoped>
.page-cover {
  width: 100%;
  min-height: 100vh;
  background: #20214b;
}

.brand-row {
  height: 44px;
}

.brand-logo {
  height: 25px;
  width: auto;
}

/* 头像：渐变紫底 + 半透明粉紫描边（对照蓝湖 66px 圆） */
.avatar-ring {
  background: linear-gradient(0deg, rgba(70, 49, 116, 1) 0%, rgba(73, 45, 96, 1) 100%);
  border: 2px solid rgba(214, 103, 230, 0.3);
}

.sign-in-link {
  color: #7b7994;
}

/* VIP 横幅：CSS 渐变（对照蓝湖 90deg 三段渐变，355x80） */
.vip-cover {
  width: 355px;
  height: 80px;
  background: linear-gradient(90deg, rgba(120, 114, 239, 1) 0%, rgba(169, 134, 254, 1) 49%, rgba(238, 155, 252, 1) 100%);
  border-radius: 8px;
  box-sizing: border-box;
}

.vip-title {
  font-weight: 500;
  font-size: 16px;
  line-height: 14px;
  color: white;
}

.vip-subtitle {
  font-weight: 400;
  font-size: 12px;
  color: #EFDCFF;
  margin-top: 6px;
}

/* Details：白底粉字胶囊 */
.details-btn {
  width: 80px;
  height: 32px;
  background: #ffffff;
  border-radius: 20px;
  font-weight: 500;
  font-size: 14px;
  color: #ff5acd;
}

/* 钻石卡：深底描边 */
.coin-card {
  height: 60px;
  background: rgba(39, 40, 88, 1);
  border: 1px solid rgba(66, 66, 109, 1);
  border-radius: 8px;
  padding: 14px 16px;
  box-sizing: border-box;
  position: relative;
  margin-top: 10px;
}

.coin-diamond {
  width: 31px;
  height: 27px;
  margin-right: 8px;
}

.coin-balance-text {
  font-size: 16px;
  font-family: HelveticaNeue-Medium, sans-serif;
  font-weight: 500;
  color: #ffffff;
}

.top-up-btn {
  height: 32px;
  border-radius: 18px;
  padding: 0 15px;
  background: linear-gradient(270deg, rgba(255, 90, 205, 1) 0%, rgba(154, 87, 255, 1) 100%);
  font-size: 14px;
  font-family: Inter-Medium, sans-serif;
  font-weight: 500;
  color: #fff;
}

/* 菜单卡：深底描边 */
.menu-card {
  background: rgba(39, 40, 88, 1);
  border: 1px solid rgba(66, 66, 109, 1);
  border-radius: 8px;
}

.username {
  height: 18px;
  font-weight: 500;
  font-size: 18px;
  color: #ffffff;
  line-height: 18px;
}

.uid {
  font-weight: 400;
  font-size: 12px;
  color: #7b7994;
  line-height: 11px;
  margin-top: 8px;
}
</style>
